import { api } from "../scripts/api.js";

/*
Admin-only "Onboard user" screen. Two cards:
  1. Onboard a user — provision a person with a ready-to-run example class
     (they own it) that enrolls the shared demo pool and pre-populates the board.
  2. Demo account pool — the small, reusable set of demo accounts. Real accounts
     (you play files as them) show genuine scores + Activity; seeded fillers just
     fill out the board. Every onboarding enrolls this same pool.

Safe replacement for the setup-professor-demo.js CLI: matches users by email, so
it never creates a duplicate account the way a mistyped CLI arg did.
*/
class Onboard extends HTMLElement {
   connectedCallback() {
      if (this._built) return;
      this._built = true;

      this.innerHTML = `
         <style>
            .onb { max-width: 680px; }
            .onb h1 { margin: 0 0 4px; }
            .onb .sub { color: #52606d; margin: 0 0 20px; }
            .onb .card { background:#fff; border:1px solid #e4e7eb; border-radius:12px; padding:20px 22px; margin-bottom:22px; }
            .onb .card h2 { margin:0 0 4px; font-size:18px; }
            .onb .card .chint { color:#7b8794; font-size:13px; margin:0 0 16px; }
            .onb .field { margin-bottom:16px; }
            .onb label { display:block; font-weight:600; margin-bottom:6px; font-size:14px; }
            .onb input[type=email], .onb input[type=text], .onb select, .onb input[type=number] {
               width:100%; box-sizing:border-box; padding:9px 11px; font-size:15px;
               border:1px solid #cbd2d9; border-radius:8px; background:#fff;
            }
            .onb .row { display:flex; gap:16px; }
            .onb .row > .field { flex:1; }
            .onb .check { display:flex; align-items:center; gap:8px; font-weight:500; }
            .onb .check input { width:auto; }
            .onb button.primary {
               background:#0f5132; color:#fff; border:none; border-radius:8px;
               padding:10px 20px; font-size:15px; font-weight:600; cursor:pointer;
            }
            .onb button.primary:disabled { opacity:.6; cursor:default; }
            .onb .result { margin-top:20px; border-radius:10px; padding:16px 18px; font-size:14px; line-height:1.5; }
            .onb .result.ok { background:#eafaf1; border:1px solid #43C59E; }
            .onb .result.err { background:#fdecea; border:1px solid #e0857f; color:#8a1c13; }
            .onb .result ul { margin:8px 0 0; padding-left:20px; }
            .onb .result a { color:#0f5132; }
            .onb .warn { color:#8a6d1c; }
            .onb .hint { color:#7b8794; font-size:12.5px; margin-top:4px; }
            .onb table.pool { width:100%; border-collapse:collapse; font-size:14px; margin-bottom:14px; }
            .onb table.pool th { text-align:left; color:#5f6368; font-weight:600; font-size:12.5px; padding:6px 8px; border-bottom:1px solid #e4e7eb; }
            .onb table.pool td { padding:8px; border-bottom:1px solid #f0f2f4; vertical-align:middle; }
            .onb .badge { font-size:12px; font-weight:600; padding:2px 9px; border-radius:12px; }
            .onb .badge.real { background:#e8f0fe; color:#1967d2; }
            .onb .badge.seed { background:#f1f3f4; color:#5f6368; }
            .onb .rm { background:none; border:1px solid #d0857f; color:#b3261e; border-radius:6px; padding:3px 10px; font-size:12.5px; cursor:pointer; }
            .onb .pool-add { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; margin-top:6px; }
            .onb .pool-add .field { margin:0; flex:1; min-width:150px; }
            .onb .pool-add .check { margin-bottom:9px; }
            .onb .pool-add button { background:#0f5132; color:#fff; border:none; border-radius:8px; padding:9px 16px; font-weight:600; cursor:pointer; }
            .onb .poolmsg { font-size:13px; margin-top:8px; }
            .onb .poolmsg.err { color:#b3261e; }
         </style>

         <section class="onb">
            <h1>Onboard a user</h1>
            <p class="sub">Set someone up with a working example class in one click: a class they own, the shared demo pool enrolled, a populated scoreboard, and a welcome email with a getting-started guide.</p>

            <div class="card">
               <h2>Onboard a user</h2>
               <p class="chint">Creates or matches the account by email (never a duplicate), makes them own an example class, enrolls the demo pool, and seeds the board.</p>

               <div class="field">
                  <label for="onb-email">Email</label>
                  <input id="onb-email" type="email" placeholder="name@university.edu" autocomplete="off" />
                  <div class="hint">Matched to any existing account by email, so no duplicate is created.</div>
               </div>

               <div class="row">
                  <div class="field">
                     <label for="onb-role">Role</label>
                     <select id="onb-role">
                        <option value="professor" selected>Professor</option>
                        <option value="admin">Admin</option>
                     </select>
                  </div>
                  <div class="field">
                     <label for="onb-fillers">Seeded fillers</label>
                     <input id="onb-fillers" type="number" min="0" max="12" value="4" />
                     <div class="hint">Fabricated board rows to pad the pool. Real accounts are managed below.</div>
                  </div>
               </div>

               <div class="field">
                  <label class="check"><input id="onb-send" type="checkbox" checked /> Email them the welcome + guide link</label>
               </div>

               <button class="primary" id="onb-go">Create example class</button>
               <div class="result" id="onb-result" hidden></div>
            </div>

            <div class="card">
               <h2>Demo account pool</h2>
               <p class="chint">Reused across every example class. <strong>Real</strong> accounts are Google logins you play files as (genuine scores + Activity); <strong>seeded</strong> fillers just fill out the board. Add your Microsoft test account and your student test account here as Real.</p>

               <table class="pool">
                  <thead><tr><th>Name</th><th>Email</th><th>Type</th><th></th></tr></thead>
                  <tbody id="pool-rows"><tr><td colspan="4">Loading...</td></tr></tbody>
               </table>

               <div class="pool-add">
                  <div class="field">
                     <label for="pool-email">Email</label>
                     <input id="pool-email" type="email" placeholder="account@gmail.com" autocomplete="off" />
                  </div>
                  <div class="field">
                     <label for="pool-label">Label (optional)</label>
                     <input id="pool-label" type="text" placeholder="MS test account" autocomplete="off" />
                  </div>
                  <label class="check"><input id="pool-seed" type="checkbox" /> Seeded filler</label>
                  <button id="pool-add-btn">Add</button>
               </div>
               <div class="poolmsg" id="pool-msg" hidden></div>
            </div>
         </section>
      `;

      this.emailEl = this.querySelector("#onb-email");
      this.roleEl = this.querySelector("#onb-role");
      this.fillersEl = this.querySelector("#onb-fillers");
      this.sendEl = this.querySelector("#onb-send");
      this.goEl = this.querySelector("#onb-go");
      this.resultEl = this.querySelector("#onb-result");

      this.poolRowsEl = this.querySelector("#pool-rows");
      this.poolEmailEl = this.querySelector("#pool-email");
      this.poolLabelEl = this.querySelector("#pool-label");
      this.poolSeedEl = this.querySelector("#pool-seed");
      this.poolAddEl = this.querySelector("#pool-add-btn");
      this.poolMsgEl = this.querySelector("#pool-msg");

      this.goEl.addEventListener("click", () => this.submit());
      this.emailEl.addEventListener("keydown", (e) => { if (e.key === "Enter") this.submit(); });
      this.poolAddEl.addEventListener("click", () => this.addPool());
      this.poolRowsEl.addEventListener("click", (e) => {
         const btn = e.target.closest("button[data-rm]");
         if (btn) this.removePool(btn.dataset.rm, btn);
      });

      this.loadPool();
   }

   show() {
      if (this._built) this.loadPool();
   }

   // ---- Onboard ----------------------------------------------------------
   async submit() {
      const email = this.emailEl.value.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
         this.showResult("err", "Please enter a valid email address.");
         return;
      }
      const body = {
         email,
         role: this.roleEl.value,
         filler_count: Number(this.fillersEl.value) || 0,
         send_email: this.sendEl.checked,
      };

      this.goEl.disabled = true;
      this.goEl.textContent = "Setting up...";
      this.resultEl.hidden = true;
      try {
         const res = await api("/admin/onboard", { method: "POST", body: JSON.stringify(body) });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(this.reasonText(data.reason) || `HTTP ${res.status}`);
         this.renderSuccess(data);
      } catch (err) {
         this.showResult("err", `Couldn't onboard: ${this.esc(err.message)}`);
      } finally {
         this.goEl.disabled = false;
         this.goEl.textContent = "Create example class";
      }
   }

   renderSuccess(d) {
      const emailLine = !d.email_sent && this.sendEl.checked
         ? `<li class="warn">Welcome email NOT sent (${this.esc(d.email_error || "email not configured")}). Send them the guide link manually: <a href="${d.guide_url}" target="_blank" rel="noopener">${d.guide_url}</a></li>`
         : d.email_sent
            ? `<li>Welcome email sent to <strong>${this.esc(d.email)}</strong> with the guide link.</li>`
            : `<li>Guide link (email skipped): <a href="${d.guide_url}" target="_blank" rel="noopener">${d.guide_url}</a></li>`;

      const roleNote = d.created_user
         ? `new ${this.esc(d.role)} account created`
         : d.prior_role && d.prior_role !== d.role
            ? `existing account raised ${this.esc(d.prior_role)} &rarr; ${this.esc(d.role)}`
            : `existing ${this.esc(d.role)} account`;

      this.showResult("ok", `
         <strong>${this.esc(d.email)} is set up.</strong>
         <ul>
            <li>${roleNote}</li>
            <li>Owns <strong>Example Class</strong> with the demo pool enrolled: <strong>${d.real_accounts} real</strong> + <strong>${d.seeded_fillers} seeded</strong> (${d.students} total).</li>
            <li><strong>${this.esc(d.assignment_title)}</strong> attached; seeded fillers populate the board, real accounts show whatever they have played.</li>
            ${emailLine}
         </ul>
         <p style="margin:10px 0 0">They can sign in at <a href="https://app.gridprof.com/web.html" target="_blank" rel="noopener">app.gridprof.com</a>, open Scoreboard, and pick the class.</p>
      `);
      this.emailEl.value = "";
   }

   // ---- Pool -------------------------------------------------------------
   async loadPool() {
      try {
         const res = await api("/admin/demo-pool");
         if (!res.ok) throw new Error(String(res.status));
         const data = await res.json();
         this.renderPool(data.members || []);
      } catch (err) {
         this.poolRowsEl.innerHTML = `<tr><td colspan="4">Couldn't load pool: ${this.esc(err.message)}</td></tr>`;
      }
   }

   renderPool(members) {
      if (!members.length) {
         this.poolRowsEl.innerHTML = `<tr><td colspan="4" style="color:#7b8794">No pool members yet. Add your test accounts below, or just run an onboarding to auto-create seeded fillers.</td></tr>`;
         return;
      }
      this.poolRowsEl.innerHTML = members.map((m) => {
         const type = m.seed_scores
            ? `<span class="badge seed">Seeded</span>`
            : `<span class="badge real">Real</span>`;
         const name = this.esc(m.label || m.display_name || "—");
         return `<tr>
            <td>${name}</td>
            <td>${this.esc(m.email)}</td>
            <td>${type}</td>
            <td style="text-align:right"><button class="rm" data-rm="${m.user_id}">Remove</button></td>
         </tr>`;
      }).join("");
   }

   async addPool() {
      const email = this.poolEmailEl.value.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
         this.poolMessage("Enter a valid email.", true);
         return;
      }
      const body = {
         email,
         label: this.poolLabelEl.value.trim(),
         seed_scores: this.poolSeedEl.checked,
      };
      this.poolAddEl.disabled = true;
      this.poolAddEl.textContent = "Adding...";
      try {
         const res = await api("/admin/demo-pool", { method: "POST", body: JSON.stringify(body) });
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || `HTTP ${res.status}`);
         this.poolEmailEl.value = "";
         this.poolLabelEl.value = "";
         this.poolSeedEl.checked = false;
         this.poolMessage("Added.", false);
         this.loadPool();
      } catch (err) {
         this.poolMessage(`Couldn't add: ${err.message}`, true);
      } finally {
         this.poolAddEl.disabled = false;
         this.poolAddEl.textContent = "Add";
      }
   }

   async removePool(userId, btn) {
      btn.disabled = true;
      btn.textContent = "...";
      try {
         const res = await api(`/admin/demo-pool/${userId}`, { method: "DELETE" });
         if (!res.ok) throw new Error(String(res.status));
         this.loadPool();
      } catch (err) {
         btn.disabled = false;
         btn.textContent = "Remove";
         this.poolMessage(`Couldn't remove: ${err.message}`, true);
      }
   }

   poolMessage(text, isErr) {
      this.poolMsgEl.textContent = text;
      this.poolMsgEl.className = `poolmsg${isErr ? " err" : ""}`;
      this.poolMsgEl.hidden = false;
   }

   // ---- helpers ----------------------------------------------------------
   reasonText(reason) {
      return {
         invalid_email: "That email is not valid.",
         no_demo_assignment: "No individual assignment with a scoreboard is available to demo. Convert/seed one first.",
         not_authorized: "You must be an admin to onboard users.",
         not_authenticated: "Please sign in again.",
         server_error: "Server error while provisioning.",
      }[reason];
   }

   showResult(kind, html) {
      this.resultEl.className = `result ${kind}`;
      this.resultEl.innerHTML = html;
      this.resultEl.hidden = false;
   }

   esc(s) {
      return String(s).replace(/[&<>"]/g, (c) =>
         ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
      );
   }
}

customElements.define("app-onboard", Onboard);
